import { Link } from 'react-router-dom';
import { useHistory, useParams } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch, useCallback, useEffect, useMemo, useState } from 'react';

import styles from './PassportGroupHeader.module.scss';
import { Button } from '../../UIPackage/MainModule/components/Button';
import { RootActions } from '../../CorePackage/MainModule/types/RootActions';
import { passportGroupIsDeletedSelector, passportGroupSelector } from '../MainModule/selectors/passportGroupSelector';
import {
  passportGroupDeleteAction,
  passportGroupExcludePassportsAction,
  passportGroupExportAction,
  passportGroupGetAction,
  passportGroupSetIsDeletedAction,
  passportGroupSetNoticeAction,
} from '../MainModule/actions/passportGroupActions';
import { PassportGroupStatusEnum } from '../../APIPackage/PassportGroupsModule/enums/PassportGroupStatusEnum';
import { getUserRoleIsProsecutor, getUserRoleIsSupervisor } from '../../AuthPackage/MainModule/selectors/authSelectors';
import { PassportGroupDetailModal } from '../PassportGroupDetailModalModule/PassportGroupDetailModal';
import { Dropdown } from '../../UIPackage/MainModule/components/Dropdown';
import { DropdownItem } from '../../UIPackage/MainModule/components/DropdownItem';
import { ExportFormatsEnum } from '../../APIPackage/ImportExportModule/enums/ExportFormatsEnum';
import { PassportGroupTypeEnum } from '../../APIPackage/PassportGroupsModule/enums/PassportGroupTypeEnum';

interface IPassportGroupHeaderProps {
  backUrl: string;
  checkedItems: { [p: number]: boolean };
  resetCheckedItems: () => void;
  onMassCancellationApply?: () => void;
  onMassCancellationOfKnoApply?: () => void;
  onMassCancellationByKnoApply?: (passportIds: number[]) => void;
  onMassDeletionApply?: () => void;
}

export const PassportGroupHeader = ({
  backUrl,
  checkedItems,
  resetCheckedItems,
  onMassCancellationApply,
  onMassCancellationOfKnoApply,
  onMassCancellationByKnoApply,
  onMassDeletionApply,
}: IPassportGroupHeaderProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const group = useSelector(passportGroupSelector);
  const isSupervisor = useSelector(getUserRoleIsSupervisor);
  const isProsecutor = useSelector(getUserRoleIsProsecutor);
  const [isShowDetailModal, setIsShowDetailModal] = useState(false);
  const isDeleted = useSelector(passportGroupIsDeletedSelector);
  const isCancellationGroup = group?.type === PassportGroupTypeEnum.CANCELLATION;
  const isDeletionGroup = group?.type === PassportGroupTypeEnum.DELETION;

  useEffect(() => {
    dispatch(passportGroupGetAction(id));
  }, [dispatch, id]);

  useEffect(() => {
    if (isDeleted) {
      dispatch(passportGroupSetIsDeletedAction(false));
      dispatch(passportGroupSetNoticeAction(`Группа № ${id} удалена`));
      history.push(backUrl);
    }
  }, [isDeleted, dispatch, history, id, backUrl]);

  const isDeleteGroupEnabled = useMemo(
    () =>
      !group?.processing &&
      !group?.deleted &&
      (group?.status === PassportGroupStatusEnum.DRAFT || group?.status === PassportGroupStatusEnum.WAITING_ACTION),
    [group],
  );

  const selectedPassportIds = useMemo(() => {
    const result: number[] = [];

    Object.keys(checkedItems).forEach(key => {
      const idNumber = Number.parseInt(key, 10);

      if (checkedItems[idNumber]) {
        result.push(idNumber);
      }
    });

    return result;
  }, [checkedItems]);

  const isEnabledExcludeDropdownItem = useMemo(
    () => !!selectedPassportIds.length && !group?.deleted && group?.status === PassportGroupStatusEnum.WAITING_ACTION,
    [selectedPassportIds, group],
  );

  const handleMassByKnoApply = useCallback(() => {
    !!selectedPassportIds.length && onMassCancellationByKnoApply && onMassCancellationByKnoApply(selectedPassportIds);
  }, [selectedPassportIds, onMassCancellationByKnoApply]);

  const handleExportXlsx = useCallback(() => {
    group && dispatch(passportGroupExportAction(group.id, ExportFormatsEnum.XLSX));
  }, [dispatch, group]);

  const handleExportOds = useCallback(() => {
    group && dispatch(passportGroupExportAction(group.id, ExportFormatsEnum.ODS));
  }, [dispatch, group]);

  const handleDelete = useCallback(() => {
    group && dispatch(passportGroupDeleteAction(group.id));
  }, [group, dispatch]);

  const handleExcludePassports = useCallback(() => {
    if (group && !!selectedPassportIds.length) {
      dispatch(passportGroupExcludePassportsAction(group.id, selectedPassportIds));
      resetCheckedItems();
    }
  }, [selectedPassportIds, dispatch, group, resetCheckedItems]);

  return (
    <>
      <div className={styles.Header}>
        <Link className={styles.BackButton} to={backUrl}>
          <span className={styles.BackButtonIcon} />
        </Link>
        <div className={styles.DescriptionBlock}>
          <div className={styles.Row}>
            <div className={styles.CommonInfo}>
              <div className={styles.TitleBlock}>
                <h3 className={styles.Title}>Группа мероприятий №{id}</h3>
                <span className={styles.InfoIcon} onClick={() => setIsShowDetailModal(true)}></span>
              </div>
              <span className={styles.Status}>
                Статус группы: {group?.statusName} {group?.error && '(ошибка)'}{' '}
                {!group?.error && group?.processing && '(в процессе)'}
              </span>
            </div>
            <div className={styles.ButtonBlock}>
              {isProsecutor && onMassCancellationApply && (
                <Button
                  color="danger"
                  text="Отменить мероприятия"
                  onClick={onMassCancellationApply}
                  disabled={!group || group.deleted || group.status !== PassportGroupStatusEnum.DRAFT}
                />
              )}
              {isProsecutor && onMassCancellationOfKnoApply && (
                <Button
                  color="danger"
                  text="Предоставить отмену КНО"
                  onClick={onMassCancellationOfKnoApply}
                  disabled={!group || group.deleted || group.status !== PassportGroupStatusEnum.DRAFT}
                />
              )}
              {isSupervisor && onMassCancellationByKnoApply && (
                <Button
                  color="danger"
                  text="Отменить мероприятия"
                  disabled={
                    !selectedPassportIds.length ||
                    group?.processing ||
                    group?.deleted ||
                    group?.status !== PassportGroupStatusEnum.CANCELLATION_BY_KNO
                  }
                  onClick={handleMassByKnoApply}
                />
              )}
              {isProsecutor && onMassDeletionApply && (
                <Button
                  color="danger"
                  text="Удалить мероприятия"
                  disabled={
                    group?.processing || group?.deleted || group?.status !== PassportGroupStatusEnum.WAITING_ACTION
                  }
                  onClick={onMassDeletionApply}
                />
              )}
              <Dropdown
                items={[
                  {
                    id: 'export-xlsx',
                    content: <DropdownItem onClick={handleExportXlsx}>Экспорт в XLSX</DropdownItem>,
                  },
                  {
                    id: 'export-ods',
                    content: <DropdownItem onClick={handleExportOds}>Экспорт в ODS</DropdownItem>,
                  },
                  ...(isDeletionGroup
                    ? [
                        {
                          id: 'exclude',
                          content: (
                            <DropdownItem isDisabled={!isEnabledExcludeDropdownItem} onClick={handleExcludePassports}>
                              Исключить из группы
                            </DropdownItem>
                          ),
                        },
                      ]
                    : []),
                  ...(isProsecutor
                    ? [
                        {
                          id: 'delete',
                          content: (
                            <DropdownItem isDanger isDisabled={!isDeleteGroupEnabled} onClick={handleDelete}>
                              Удалить группу
                            </DropdownItem>
                          ),
                        },
                      ]
                    : []),
                ]}
              />
            </div>
          </div>
          <div className={styles.CountBlock}>
            <span className={styles.Count}>
              Всего: {group?.totalCount || 0} {isCancellationGroup ? 'Отменено' : 'Удалено'}: {group?.count || 0}{' '}
              Ожидают {isCancellationGroup ? 'отмены' : 'удаления'}: {group ? group?.totalCount - group?.count : 0}
            </span>
            <span className={styles.Count}>Выбрано: {selectedPassportIds.length}</span>
          </div>
        </div>
      </div>
      {isShowDetailModal && <PassportGroupDetailModal group={group} onClose={() => setIsShowDetailModal(false)} />}
    </>
  );
};
